import dayjs from "dayjs";

const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const FORMATTED_DATE_PATTERN = /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/;
const NUMERIC_DATE_PATTERN = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[T\s].*)?$/;

const MONTH_NUMBERS = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
};

// A 2-digit year parsed with a 4-digit token (YYYY) is stored literally: 09 → 0009.
// YY parsing maps 00–68 to 2000–2068 and 69–99 to 1969–1999. Apply that before display.
const expandTwoDigitYear = (yearDigits) => {
  const year = Number(yearDigits);
  if (!Number.isInteger(year) || year > 99) return null;
  return year > 68 ? 1900 + year : 2000 + year;
};

const normalizeShortIsoYear = (value) => {
  const expandedYear = expandTwoDigitYear(value.slice(0, 4));
  if (expandedYear === null) return value;
  return `${expandedYear}${value.slice(4)}`;
};

const isDobField = (fieldId) => {
  const key = String(fieldId || "").toLowerCase().replace(/[^a-z]/g, "");
  return key === "dob" || key === "dateofbirth";
};

const pad2 = (value) => String(value).padStart(2, "0");

const formatDobParts = (month, day, yearDigits) => {
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  if (
    !Number.isInteger(monthNumber) ||
    !Number.isInteger(dayNumber) ||
    monthNumber < 1 ||
    monthNumber > 12 ||
    dayNumber < 1 ||
    dayNumber > 31
  ) {
    return null;
  }

  const expandedYear = expandTwoDigitYear(String(yearDigits));
  const year = expandedYear === null ? String(yearDigits).padStart(4, "0") : String(expandedYear);
  const parsed = dayjs(`${year}-${pad2(monthNumber)}-${pad2(dayNumber)}`);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : null;
};

export const formatPreviewDateValue = (value, fieldId) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  const isDob = isDobField(fieldId);

  if (ISO_DATE_PATTERN.test(trimmed)) {
    const parsed = dayjs(normalizeShortIsoYear(trimmed));
    if (!parsed.isValid()) return value;
    return parsed.format("DD MMM YYYY");
  }

  if (isDob) {
    const numeric = trimmed.match(NUMERIC_DATE_PATTERN);
    if (numeric) {
      const formatted = formatDobParts(numeric[1], numeric[2], numeric[3]);
      if (formatted) return formatted;
    }
  }

  const formatted = trimmed.match(FORMATTED_DATE_PATTERN);
  if (!formatted) return value;

  const [, day, monthName, yearDigits] = formatted;
  const expandedYear = expandTwoDigitYear(yearDigits);
  const year = expandedYear === null ? yearDigits : String(expandedYear);

  if (!isDob) {
    if (expandedYear === null) return value;
    return `${day} ${monthName} ${year}`;
  }

  const month = MONTH_NUMBERS[monthName.toLowerCase()];
  if (!month) return `${pad2(day)} ${monthName} ${year}`;
  return formatDobParts(month, day, year) || `${pad2(day)}-${month}-${year}`;
};

export const matchesReportSearch = (query, ...parts) => {
  const terms = String(query).trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;

  const haystack = parts.filter(Boolean).join(" ").toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

export const compareAlphaLabels = (a, b) => {
  const normalize = (value) =>
    String(value ?? "")
      .replace(/\s*\([^)]*\)\s*$/, "")
      .replace(/\s+/g, " ")
      .trim();
  return normalize(a).localeCompare(normalize(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const formatLabelSpacing = (text) => {
  if (text == null || text === "") return text;
  return String(text)
    .replace(/([^\s(])\(/g, "$1 (")
    .replace(/\(\s+/g, "(")
    .replace(/\s+\)/g, ")")
    .replace(/\)([^\s),])/g, ") $1")
    .replace(/\s*\/\s*/g, " / ")
    .replace(/[ \t]+/g, " ")
    .trim();
};

export const formatReportFieldLabel = (field) => {
  const raw = (field?.label || "").replace(/\s+/g, " ").trim();
  if (!raw) return field?.id || "";

  if (!field?.badge) return formatLabelSpacing(raw);

  const spacedBadge = String(field.badge).trim();
  const suffix = ` (${spacedBadge})`;
  const formatted = formatLabelSpacing(raw);
  if (formatted.endsWith(suffix)) return formatted;
  return formatLabelSpacing(`${raw} (${spacedBadge})`);
};

export const normalizeReportColumnIds = (cols, fields) => {
  if (!Array.isArray(cols)) return [];
  if (!fields?.length) return cols;

  const byId = new Map();
  const byLabel = new Map();

  fields.forEach((field) => {
    if (!field?.id) return;
    byId.set(field.id, field.id);
    byId.set(String(field.id).toLowerCase(), field.id);
    if (field.label) {
      byLabel.set(String(field.label).toLowerCase(), field.id);
    }
  });

  return cols.map((col) => {
    const value = String(col ?? "").trim();
    if (!value) return value;
    if (byId.has(value)) return byId.get(value);

    const lower = value.toLowerCase();
    if (byId.has(lower)) return byId.get(lower);
    if (byLabel.has(lower)) return byLabel.get(lower);

    const camel = value.includes("_")
      ? value.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
      : value;
    if (byId.has(camel)) return byId.get(camel);
    if (byId.has(camel.toLowerCase())) return byId.get(camel.toLowerCase());

    return camel;
  });
};

export const readGenerateReportRows = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data?.rows)) return data.data.rows;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  if (data?.rows && typeof data.rows === "object") return Object.values(data.rows);
  return [];
};

export const remapReportRowsToFieldIds = (
  rows,
  columnIds,
  fields,
  apiCols = []
) => {
  if (!Array.isArray(rows)) return [];

  const fieldList = fields || [];
  const idByKey = new Map();
  fieldList.forEach((field) => {
    if (!field?.id) return;
    idByKey.set(field.id, field.id);
    idByKey.set(String(field.id).toLowerCase(), field.id);
    if (field.label) idByKey.set(String(field.label).toLowerCase(), field.id);
  });

  const resolvedApiCols = normalizeReportColumnIds(apiCols, fieldList);

  return rows.map((row) => {
    if (Array.isArray(row)) {
      const keys =
        resolvedApiCols.length === row.length
          ? resolvedApiCols
          : columnIds.length === row.length
            ? columnIds
            : columnIds;
      return keys.reduce((acc, id, index) => {
        if (id !== undefined) acc[id] = row[index];
        return acc;
      }, {});
    }

    if (!row || typeof row !== "object") return row;

    const mapped = {};
    const lowerKeys = new Map(
      Object.keys(row).map((key) => [key.toLowerCase(), key])
    );

    Object.entries(row).forEach(([key, value]) => {
      const id = idByKey.get(key) || idByKey.get(String(key).toLowerCase()) || key;
      mapped[id] = value;
    });

    fieldList.forEach((field) => {
      if (mapped[field.id] !== undefined) return;
      const match =
        lowerKeys.get(field.id.toLowerCase()) ||
        lowerKeys.get(String(field.label || "").toLowerCase());
      if (match !== undefined) mapped[field.id] = row[match];
    });

    if (
      Array.isArray(apiCols) &&
      apiCols.length > 0 &&
      Array.isArray(columnIds) &&
      apiCols.length === columnIds.length
    ) {
      apiCols.forEach((col, index) => {
        const id = columnIds[index];
        if (!id || mapped[id] !== undefined) return;
        if (row[col] !== undefined) {
          mapped[id] = row[col];
          return;
        }
        const match = lowerKeys.get(String(col).toLowerCase());
        if (match !== undefined) mapped[id] = row[match];
      });
    }

    return mapped;
  });
};

export const getReportRowValue = (row, fieldId, fields) => {
  if (!row || typeof row !== "object") return undefined;
  if (row[fieldId] !== undefined) return row[fieldId];

  const field = (fields || []).find((item) => item.id === fieldId);
  const candidates = [field?.label, field?.id];
  const lowerKeys = new Map(
    Object.keys(row).map((key) => [key.toLowerCase(), key])
  );

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (row[candidate] !== undefined) return row[candidate];
    const match = lowerKeys.get(String(candidate).toLowerCase());
    if (match !== undefined) return row[match];
  }

  return undefined;
};
